import { canUseDOM } from "utils/helpers/can-use-dom";
import { Vec2 } from "utils/math/vec2";
import { Mesh2d } from "./buffers";
import { Camera2d } from "./camera-2d";
import { Material } from "./material";
import { Shader } from "./shader";
import { Texture } from "./texture";
import { Webgl2Component, TextureComponent } from "./webgl2-component";
import { webgl2ShadersMap } from "./webgl2.constants";
import { Webgl2Shaders, Webgl2Materials } from "./webgl2.types";

export class Webgl2 {
  private static _canvas: HTMLCanvasElement;
  public static get canvas() {
    return this._canvas;
  }

  private static _gl: WebGL2RenderingContext;
  public static get gl() {
    return this._gl;
  }

  private static _fb: WebGLFramebuffer;
  private static _fbTexture: Texture;
  private static _fbComponent: TextureComponent;

  public static readonly camera: Camera2d = new Camera2d();

  private static _isInitialized = false;
  public static get isInitialized() {
    return this._isInitialized;
  }

  public static readonly shaders: Webgl2Shaders = {} as any;
  public static readonly materials: Webgl2Materials = {} as any;

  public static initialize(): Promise<{ gl: WebGL2RenderingContext }> {
    return new Promise(async (resolve, reject) => {
      if (this._isInitialized) return resolve({ gl: this.gl });

      if (!canUseDOM) {
        return reject(new Error("Couldn't use DOM, please make sure that you have access to DOM."));
      }

      const canvas = document.createElement("canvas");
      const gl = canvas.getContext("webgl2");

      if (!gl) {
        return reject(
          new Error(
            "Couldn't get webgl2 context, please make sure that you have latest drivers, " +
              "have enabled webgl2 feature and it's supported by your device or browser",
          ),
        );
      }

      // if (!gl.getExtension("EXT_color_buffer_float")) {
      //   return reject(new Error("Can't render to floating point textures"));
      // }
      //
      // if (!gl.getExtension("OES_texture_float_linear")) {
      //   return reject(new Error("Can't filter floating point textures"));
      // }

      // NOTE: parameters which possible to check for full support.
      // const params = [
      //   { pname: "MAX_3D_TEXTURE_SIZE", min: 256 },
      //   { pname: "MAX_DRAW_BUFFERS", min: 4 },
      //   { pname: "MAX_COLOR_ATTACHMENTS", min: 4 },
      //   { pname: "MAX_VERTEX_UNIFORM_BLOCKS", min: 12 },
      //   { pname: "MAX_VERTEX_TEXTURE_IMAGE_UNITS", min: 16 },
      //   { pname: "MAX_FRAGMENT_INPUT_COMPONENTS", min: 60 },
      //   { pname: "MAX_UNIFORM_BUFFER_BINDINGS", min: 24 },
      //   { pname: "MAX_COMBINED_UNIFORM_BLOCKS", min: 24 },
      // ];
      //
      // for (const param of params) {
      //   const value = gl.getParameter((gl as any)[param.pname]);
      //
      //   if (typeof value !== "number" || Number.isNaN(value) || value < param.min) {
      //     return reject(new Error("Webgl2 context has unsupported parameters"));
      //   }
      // }

      this._isInitialized = true;

      this._canvas = canvas;
      this._gl = gl;
      this.setViewport(canvas.width, canvas.height);

      this._prepareInstances();

      this._fb = this.gl.createFramebuffer() as WebGLFramebuffer;
      this._fbTexture = new Texture();
      this._fbComponent = new TextureComponent();

      return resolve({ gl: this.gl });
    });
  }

  public static setViewport(vec: Vec2): void;
  public static setViewport(width: number, height: number): void;
  public static setViewport(a: any, b?: any): void {
    const w = typeof a === "number" ? a : a.width;
    const h = typeof b === "number" ? b : a.height;

    if (this.camera.projection.equals(w, h)) return;

    this._canvas.width = w;
    this._canvas.height = h;
    this._gl.viewport(0, 0, w, h);
    this.camera.updateProjection(w, h);
    this.clear();
  }

  public static clear() {
    this.gl.clearStencil(0);
    this.gl.clearColor(0, 0, 0, 0);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT | this.gl.STENCIL_BUFFER_BIT);
  }

  public static renderMesh(mesh: Mesh2d, material: Material): void {
    material.setUniforms({
      u_camScale: this.camera.scale,
      u_camViewProjection: this.camera.viewProjectionMatrix,
      u_resolution: this.camera.projectionArr,
    });

    material.bind();
    mesh.bind();

    if (mesh.isIndexed) {
      this.gl.drawElements(mesh.drawMode, mesh.count, this.gl.UNSIGNED_SHORT, 0);
    } else {
      this.gl.drawArrays(mesh.drawMode, 0, mesh.count);
    }

    material.clearMaterial();

    this.gl.bindTexture(this.gl.TEXTURE_2D, null);
  }

  public static renderComponent<C extends Webgl2Component<any, any>>(
    component: C,
    ...args: C extends Webgl2Component<infer P, any> ? (P extends undefined ? [] : [props: P]) : []
  ): void {
    const renderData = component.getRenderData(args[0] as any);
    if (!renderData) return;
    component.calcModelMatrix();
    this._renderComponent(component, ...args);
  }

  public static bindTextureToFramebuffer(texture: Texture, callback: VoidFunction) {
    // Note: don't render to framebuffer with zero size.
    if (texture.textureSize.hasZero()) return;

    const prevViewport = this.camera.projection;
    this.camera.save();

    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this._fb);

    const { width, height } = texture.textureSize;
    this._gl.viewport(0, 0, width, height);
    this.camera.reset(width, height);

    this._fbTexture.allocateTexture(width, height);
    this.gl.framebufferTexture2D(
      this.gl.FRAMEBUFFER,
      this.gl.COLOR_ATTACHMENT0,
      this.gl.TEXTURE_2D,
      this._fbTexture.id,
      0,
    );

    callback();

    this.gl.framebufferTexture2D(
      this.gl.FRAMEBUFFER,
      this.gl.COLOR_ATTACHMENT0,
      this.gl.TEXTURE_2D,
      texture.id,
      0,
    );

    this._fbComponent.transform.setSize(width, height);
    this.renderComponent(this._fbComponent, { texture: this._fbTexture });

    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);

    this.setViewport(prevViewport);
    this.camera.restore();
  }

  private static _renderComponent<C extends Webgl2Component<any, any>>(
    component: C,
    ...args: C extends Webgl2Component<infer P, any> ? (P extends undefined ? [] : [props: P]) : []
  ): void {
    if (component.style.display === "none") return;

    const renderData = component.getRenderData(args[0] as any);

    if (!renderData) return;

    renderData.material.setUniforms({
      u_model: component.getModelMatrix(),
      u_shapeId: component.u_id,
    });

    this.renderMesh(Mesh2d.meshesMap.rectIndexedMesh, renderData.material);

    component.forEachChildren((child) => {
      this._renderComponent(child);
    });
  }

  private static _prepareInstances() {
    for (const shaderMapKey in webgl2ShadersMap) {
      const key = shaderMapKey as keyof typeof webgl2ShadersMap;
      const [vertex, fragment] = webgl2ShadersMap[key];
      const shader = new Shader(shaderMapKey, vertex, fragment);
      const material = new Material(shader);
      this.shaders[key] = shader as any;
      this.materials[key] = material as any;
    }

    Texture.initializeTextureData();
    Mesh2d.initializeMeshes();
  }
}

export default Webgl2;
